import { parsePhoneNumberFromString, isValidNumberForRegion } from 'libphonenumber-js'
import * as EmailValidator from 'email-validator';
import { ContactModel, BusinessModel, LocationModel, ResellerModel, UserModel, ContactHistoryModel, ContactHistoryCampaignModel, ContactSmsRequestProcessModel, ContactEmailRequestProcessModel, ReviewRequestCampaignModel, ReviewTargetModel } from '@models/index';
import { ADDRESSES } from './data-mock';
import { intercomAppId } from './data-utils';
import { ReviewTargetSource } from '@enums/reviewTargetSource.enum';
import { environment } from '@environments/environment';
import { FacebookErrorModel } from '@models/facebookError.model';
import { FacebookTokenTypes } from '@enums/facebookTokenTypes.enum';

/**
 * @ignore
 */
export type FilterByqueryData = BusinessModel | LocationModel | ContactModel | ResellerModel;

/**
 * Returns if a string includes other characters different from
 * the space one.
 * <pre>
 * <code class="language-typescript">
 * //Returns true
 * isNotEmpty = (str: 'example')
 * //Returns false
 * isNotEmpty = (str: '   ')
 * </code>
 *  </pre>
 * @param str Contains the string to be evaluated
 */
export const isNotEmpty = (str: string): boolean => str ? str.trim().length > 0 ? true : false : false

export const filterByQuery = (dataToCheck: FilterByqueryData[], propetiesToCheck: string[], query: string) => query.length ?
  dataToCheck.filter((data) =>
    propetiesToCheck.find((property) => data[property] ? data[property].toLocaleLowerCase().includes(query.toLocaleLowerCase()) ? true : false : false)
  )
  : dataToCheck;


export const changeUserRolesName = (userRolename: string) =>
  userRolename = userRolename.includes('ADMIN') ? 'BUSINESS ADMIN' : 'USER'

export const addRandomImageToBusiness = (item: BusinessModel) => ({
  ...item,
  bgImage: `url(./assets/images/originals/abstract${Math.floor(Math.random() * 11)}.jpg)`
})

export const addRandomImageToLocation = (item: LocationModel) => ({
  ...item,
  bgImage: `url(./assets/images/originals/abstract${Math.floor(Math.random() * 11)}.jpg)`
})

export const addRandomImageToReseller = (item: ResellerModel) => ({
  ...item,
  bgImage: `url(./assets/images/originals/abstract${Math.floor(Math.random() * 11)}.jpg)`
})

export const businessMock = (business: BusinessModel, index: number) => Object.assign({}, business, {
  countLocations: Math.floor(Math.random() * 100),
  mainAddress: ADDRESSES[index],
  countReviews: Math.floor(Math.random() * 100),
  countSentReviewRequests: Math.floor(Math.random() * 900) + 100,
})

export const showIntercom = (user: UserModel, parentEntityName?: string) => {
  (<any>window).Intercom("update", {
    app_id: intercomAppId,
    email: user.email,
    Business: parentEntityName,
    First_Name: user.firstName,
    Last_Name: user.lastName,
    hide_default_launcher: false
  });
}

export const hideIntercom = () => {
  (<any>window).Intercom("update", {
    hide_default_launcher: true
  });
}

export const showIntercomChat = (user: UserModel, parentEntityName?: string) => {
  (<any>window).Intercom("show");
}

const formatAmPm = (date: Date) => {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  let ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 ? hours : 12; // the hour '0' should be '12'
  const strMinutes = minutes < 10 ? '0' + minutes : minutes;
  return hours + ':' + strMinutes + ' ' + ampm;
}

export const mapTime = (timeNow: number) => (createTime: number): string => {
  const timeInMin: number = (timeNow - createTime) / 60000;
  const dateCreated = new Date(createTime);

  if (Math.floor(timeInMin) <= 0) {
    return "a moment ago"
  } else if (0 < timeInMin && timeInMin < 60) {
    return `${Math.floor((timeInMin))} minutes ago`
  } else if (timeInMin > 60 && timeInMin < 1440) {
    return `${Math.floor((timeInMin / 60))} hours ago`
  } else if (timeInMin >= 1440 && timeInMin <= (2 * 1440)) {// 1440m = 24h
    return `yesterday at ${formatAmPm(dateCreated)}`
  } else {
    return dateCreated.toDateString()
  }
}

export const sortContact = (item1: ContactModel, item2: ContactModel) => {
  return item2.created - item1.created || item2.name.localeCompare(item1.name) || item2.lastName.localeCompare(item1.lastName)
}

export const isEmpty = (val: string): boolean => {
  return (!val || /^\s*$/.test(val));
}

export const allowedCountries: string[] = ['US', 'CA', 'GB', 'AU', 'ZA']

export const validateCountry = (phoneNumber: string): boolean => {
  const dataPhone = parsePhoneNumberFromString(phoneNumber)
  return dataPhone.country
    ? allowedCountries.indexOf(dataPhone.country as string) !== -1
    && isValidNumberForRegion(dataPhone.nationalNumber, dataPhone.country)
    : false
}

export const isValidPhoneNumber = (val: string): boolean => {
  return parsePhoneNumberFromString(val.replace(/[^0-9+]/g, '')) && validateCountry(val)
}

export const filterContacts = (items: ContactModel[]): ContactModel[] => {
  return items
    .filter(item => !isEmpty(item.name))
    .filter(item => !isEmpty(item.email) || !isEmpty(item.phoneNumber))
    .filter(item =>
      (isEmpty(item.email) || EmailValidator.validate(item.email)) &&
      (isEmpty(item.phoneNumber) || isValidPhoneNumber(item.phoneNumber))
    )
}

/**
 * Pause execution in an async context until a particular expression is met. Originally built to
 * wait until the Google Auth API was loaded from an external script tag, but is no longer used for
 * that purpose.
 *
 * Interally, the expression is checked every `millis` milliseconds, defaulting to 1000 (1 second).
 */
export async function until<T>(getExpression: () => T, millis: number = 1000): Promise<T> {
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      // hoist var for better perf; we don't know how long this will run
      var expression = getExpression();
      if (expression) {
        clearInterval(interval);
        resolve(expression);
      }
    }, millis);
  });
};


// Return clickable pages of the paginator
export const calcPagesInterval = (pagesArray: number[], selectedPage: number, lastSelectedPage: number): { pageStart: number, pageEnd: number } => {
  const numNegativeOnes = pagesArray.filter(item => item == -1).length;

  if (numNegativeOnes == 0) {
    return { pageStart: pagesArray[0], pageEnd: pagesArray[pagesArray.length - 1] };
  }

  if (numNegativeOnes == 1 && pagesArray.findIndex(item => item == -1) == pagesArray.length - 2) {
    return {
      pageStart: pagesArray[0],
      pageEnd: pagesArray[pagesArray.findIndex(item => item == -1) - 1]
    };
  }

  if (numNegativeOnes == 2 && selectedPage > lastSelectedPage) {
    return {
      pageStart: pagesArray[pagesArray.findIndex(item => item == -1) + 1],
      pageEnd: pagesArray[pagesArray.lastIndexOf(-1) - 1]
    };
  }

  if (numNegativeOnes == 2 && selectedPage < lastSelectedPage) {
    return {
      pageStart: pagesArray[pagesArray.lastIndexOf(-1) - 1],
      pageEnd: pagesArray[pagesArray.findIndex(item => item == -1) + 1]
    };
  }

  if (numNegativeOnes == 1 && pagesArray.findIndex(item => item == -1) == 1) {
    return {
      pageStart: pagesArray[pagesArray.length - 1],
      pageEnd: pagesArray[pagesArray.findIndex(item => item == -1) + 1]
    };
  }
}

//Conditional logic for returning a +1 list only if ther is not a previus match //TODO this should already exists elsewhere, lodash?
export const addElementUsingPredicate =
  <E>
    (predicate: (element: E, elements: Array<E>) => boolean) =>
    (elements: Array<E>, element: E) =>
      (elements.length == 0) ? [element] :
        predicate(element, elements) ? elements :
          [...elements, element]



//Filters defined as simple functions
export const trueFn = (c: ContactModel): boolean => true
export const falseFn = (c: ContactModel): boolean => false
export const filterByEmail = (s: String) => (c: ContactModel): boolean => c.email == s
export const filterByphone = (s: String) => (c: ContactModel): boolean => c.phoneNumber == s
export const filterByEmailOrPhone = (e: String) => (p: String) => (c: ContactModel): boolean => filterByEmail(e)(c) || filterByphone(p)(c)

//Predicate that finds a match depending on email and phoneNumber emptyness
export const isContactInArray = (contact: ContactModel, contacts: Array<ContactModel>): boolean => {
  var { email, phoneNumber } = contact
  return contacts.some(
    isNotEmpty(email) && isNotEmpty(phoneNumber) ? filterByEmailOrPhone(email)(phoneNumber) :
      isNotEmpty(email) ? filterByEmail(email) :
        isNotEmpty(phoneNumber) ? filterByphone(phoneNumber) :
          falseFn //special else case -> do not add.
  )
}
/**
 * A composed reduce -> addByPredicate -> isContactInArray to remove dupliclates
 * @param items to be passed to the new non duplicated result set
 */

export const removeDuplicatesReducer = addElementUsingPredicate(isContactInArray)

export const removeContactDuplicates = (contactList: ContactModel[]): ContactModel[] => {
  return contactList.reduce(removeDuplicatesReducer, [])
}

export const isGoogle = (name: string) => {
  return name ?
    name.toLowerCase().includes('google')
    : false
}

export const isReviewFromGMB = (source: string) => {
  return source ?
    source.includes(ReviewTargetSource.googleMyBusiness)
    : false
}

// Returns true if any review target in the list has googleMyBusiness as a source
export const hasGoogleMyBusinessSource = (reviewTargets: ReviewTargetModel[]): boolean => {
  return Array.isArray(reviewTargets)
    ? reviewTargets.some(rt => rt.source == ReviewTargetSource.googleMyBusiness)
    : false;
}


export const isGMBEnabled = (locationId: string) => {
  return environment.gmb_enabled &&
    (environment.gmb_whitelist.length == 0 ||
      environment.gmb_whitelist.some(item => item == locationId)
    ) ? true
    : false
}

/**
 * Returns if a string contains the 'facebook'
 * string on it.
 * @param name The string to be evaluated
 */
export const isFacebook = (name: string) => {
  return name ?
    name.toLowerCase().includes('facebook')
    : false
}

/**
 * Returns if Facebook feture is enabled for a selected
 * location
 * based on facebookEnabled and facebook_whitelist
 * properties set on either environment.ts or
 * environment.prod.ts
 * @param locationId The location id to be evaluated
 */
export const isFacebookEnable = (locationId: string) => {
  return environment.facebookEnabled &&
    (environment.facebook_whitelist.length == 0 ||
      environment.facebook_whitelist.some(item => item == locationId)
    ) ? true
    : false
}

/**
 * Returns wheter a review is pulled with facebook
 * integration or not
 * @param source The source from where the review was pulled in.
 */
export const isReviewFromFacebook = (source: string) => {
  return source ?
    source.includes(ReviewTargetSource.facebook)
    : false
}


/**
 * Returns an updated Facebook Error Model 
 * based on the HTTP status error that comes
 * into dataError.
 * @param dataError Contains the HTTP request error returned by GMR API
 */
export const facebookErrorMapper = (dataError: FacebookErrorModel): FacebookErrorModel => {
  switch (dataError.status) {
    case 400: return {
      ...dataError,
      error: Object.assign({}, dataError.error, { error_user_msg: dataError.error.error_user_msg + '. Code: ' + dataError.error.loggerCode }),
      showable: true,
      type: 'pop-up',
      ok_action: true
    }
    default:
      return {
        ...dataError,
        error: Object.assign({}, dataError.error, { error_user_msg: dataError.error.error_user_msg + '. Code: ' + dataError.error.loggerCode }),
        showable: false,
        type: ''
      }
  }
}
/**
 * Returns the list of Facebook needed permissions based on
 * the service the user wants to integrate.
 * @param typeService Contains the type of service the user wants to integrate.
 */
export const facebookScope = (typeService: string) => {
  return typeService == FacebookTokenTypes.SOCIAL_SHARING_RECOMMENDATIONS ?
    'business_management,pages_show_list,pages_manage_posts,pages_read_engagement, pages_read_user_content, pages_manage_engagement'
    : 'business_management,pages_show_list,pages_manage_posts,pages_read_engagement'
}

export const showSentimentCheck = (createDate: number): boolean => {
  return createDate < environment.sentimentCheckDeprecationDate
}

export const showStars = (countStars: number[]): boolean => {
  return countStars.reduce((acc, stars) => acc += stars, 0) > 0 ? true : false
}
export const showThumbs = (countThumbs: number[]): boolean => {
  return countThumbs.reduce((acc, thumbs) => acc += thumbs, 0) > 0 ? true : false
}
export const showFaces = (countFaces: number[]): boolean => {
  return countFaces.reduce((acc, faces) => acc += faces, 0) > 0 ? true : false
}

export const showSentimentCheckByLenght = (countStars: number[], countThumbs: number[], countFaces: number[]): boolean => {
  return showStars(countStars) || showThumbs(countThumbs) || showFaces(countFaces)
}

export const removeFeedbackButton = (feedbackHtml: string, enablePrivateFeedback: boolean): string => {
  if (enablePrivateFeedback) {
    return feedbackHtml
  }
  const index = feedbackHtml.indexOf("Review Check us Out Sites </strong></p>")
  return feedbackHtml.slice(0, index + 39)
}


export const remainingTrialDays = (endDate: number, nowDate: number) : string => {
    return endDate - nowDate  <= 0
        ? 'today'
        : (endDate - nowDate != 86400)
            ? 'in ' + (endDate - nowDate)/86400 + ' days' 
            : 'in ' + (endDate - nowDate)/86400 + ' day'
}

export const filterByCampaigns = (contactHistory: ContactHistoryModel, campaigns?: string[]): ContactHistoryModel => {
  if (campaigns == null) {
    return contactHistory
  } else if (campaigns.length == 0) {
    return contactHistory
  }
  const campaignResults = contactHistory.campaigns.filter(item => {
    return campaigns.includes(item.campaignId)
  })
  return {
    ...contactHistory,
    campaigns: campaignResults
  }
}

export const getMostRecentReviewRequest = (sms?: ContactSmsRequestProcessModel, email?: ContactEmailRequestProcessModel): number => {
  let smsNew = []
  let emailNew = []
  if (sms != null) {
    smsNew = [...sms.sentReminders, sms.sentReviewRequest].filter(item => item != undefined)
  }
  if (email != null) {
    emailNew = [...email.sentReminders, email.sentReviewRequest].filter(item => item != undefined)
  }
  const allItems = [...smsNew, ...emailNew]
  return Math.max(...allItems)
}

export const getMostRecentStatusCampaigns = (campaigns: ContactHistoryCampaignModel[]): string => {
  if (campaigns.length == 1) {
    return campaigns[0].status
  }
  return campaigns.map(item => {
    return {
      ...item,
      mostRecent: getMostRecentReviewRequest(item.sms, item.email)
    }
  }).sort((a, b) => (a.mostRecent < b.mostRecent) ? 1 : (a.mostRecent > b.mostRecent) ? -1 : 0)
    .map(item => item.status)[0]
}

export const filterByStatusCampaigns = (contactHistory: ContactHistoryModel, status?: string[]): ContactHistoryModel => {
  if (status == null) {
    return contactHistory
  } else if (status.length == 0) {
    return contactHistory
  }
  const campaignResults = contactHistory.campaigns.filter(item => {
    return status.includes(item.status)
  })
  return {
    ...contactHistory,
    campaigns: campaignResults
  }
}

export const sortCampaign = (item1: ReviewRequestCampaignModel, item2: ReviewRequestCampaignModel) => {
  return item1.createDate - item2.createDate || item1.generalSettings.name.localeCompare(item2.generalSettings.name)
}
